/**
 * Bank Entity Service - Integration Management
 *
 * Handles external system integrations including:
 * - API connectivity
 * - Third-party data providers
 * - Health monitoring
 * - Configuration management
 */

import {
  BankIntegrationConfig,
  IntegrationHealthStatus,
  IntegrationOverviewSummary,
} from '@/types/entities/bank';

// ====== INTEGRATION SERVICE ======

class BankIntegrationService {
  private baseUrl = '/api/bank/integration';
  private useMockApi = import.meta.env.VITE_USE_MOCK_API === 'true';
  private isDevelopment = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';

  /**
   * Get integration configuration
   */
  async getIntegrationConfig(bankId: string): Promise<BankIntegrationConfig> {
    if (this.useMockApi || this.isDevelopment) {
      return this.getMockIntegrationConfig();
    }

    try {
      const response = await fetch(`${this.baseUrl}/config/${bankId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.warn('Integration API not available, using mock data');
        return this.getMockIntegrationConfig();
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching integration config:', error);
      return this.getMockIntegrationConfig();
    }
  }

  /**
   * Get integration health status
   */
  async getHealthStatus(bankId: string): Promise<IntegrationHealthStatus> {
    if (this.useMockApi || this.isDevelopment) {
      return this.getMockHealthStatus();
    }

    try {
      const response = await fetch(`${this.baseUrl}/health/${bankId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.warn('Health API not available, using mock data');
        return this.getMockHealthStatus();
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching health status:', error);
      return this.getMockHealthStatus();
    }
  }

  private getMockIntegrationConfig(): BankIntegrationConfig {
    return {
      bankId: 'bank-001',
      enabled: true,
      mode: 'sandbox',
      credentials: {
        clientId: 'test-client-id',
        apiKeyLastFour: '****1234',
        rotationPolicy: {
          automaticRotation: false,
        },
        storedSecurely: true,
        credentialStatus: 'valid',
      },
      api: {
        baseUrl: 'https://api.bank.com/v1',
        loans: '/loans',
        proposals: '/proposals',
        collateral: '/collateral',
        pricing: '/pricing',
        documents: '/documents',
        auth: '/auth',
        health: '/health',
      },
      auth: {
        method: 'oauth2_client_credentials',
        oauth: {
          tokenUrl: 'https://api.bank.com/oauth/token',
          scopes: ['loans.read', 'loans.write', 'proposals.read'],
          expiresIn: 3600,
          refreshTokenSupported: true,
        },
        lastAuthStatus: 'success',
      },
      webhooks: {
        enabled: false,
        subscribedEvents: [],
        retryPolicy: {
          strategy: 'exponential',
          maxRetries: 3,
          initialDelayMs: 1000,
          maxDelayMs: 30000,
          jitter: true,
          retryOn: [],
        },
        deliveryStats: {
          delivered: 0,
          failed: 0,
          successRate: 0,
          averageLatencyMs: 0,
          retries: 0,
        },
      },
      dataSync: {
        schedule: {
          mode: 'daily',
          dailyTimeUTC: '02:00',
        },
        resources: [],
        conflictResolution: 'remote_wins',
        incrementalSupported: true,
        initialSyncCompleted: true,
        lastSyncStatus: 'success',
      },
      thirdPartyProviders: [],
      fieldMappings: {
        proposal: {},
        resident: {},
        collateral: {},
        loan: {},
      },
      retryPolicy: {
        strategy: 'exponential',
        maxRetries: 3,
        initialDelayMs: 1000,
        maxDelayMs: 30000,
        jitter: true,
        retryOn: [],
      },
      security: {
        encryptionInTransit: true,
        encryptionAtRest: true,
        piiIncluded: true,
        dataMasking: true,
        ipAllowlistEnabled: false,
        signingRequired: true,
        vulnerabilitiesOpen: 0,
      },
      compliance: {
        pci: 'certified',
        soc2: 'certified',
        iso27001: 'certified',
        glba: 'certified',
      },
      lastUpdated: new Date(),
      updatedBy: 'system',
      health: this.getMockHealthStatus(),
    };
  }

  private getMockHealthStatus(): IntegrationHealthStatus {
    return {
      overall: 'operational',
      lastChecked: new Date(),
      latencyMs: 125,
      components: [
        {
          component: 'auth',
          status: 'operational',
          latencyMs: 95,
          lastSuccess: new Date(),
        },
        {
          component: 'proposals_api',
          status: 'operational',
          latencyMs: 150,
          lastSuccess: new Date(),
        },
      ],
      incidentsOpen: 0,
      statusHistory: [],
    };
  }
}

export const bankIntegrationService = new BankIntegrationService();
